import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getSetupIntent, getPaymentMethods } from 'school-space-client/api';
import { UserContext } from 'school-space-client/components';
import SetupForm from './setupForm';
import Deposit from './deposit';
import customersGoCard from './customersGoCard.json';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

function PaymentStatusFeedback({ onFinish }) {
    const stripe = useStripe();
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                if (!stripe) return;
                const clientSecret = new URLSearchParams(window.location.search).get(
                    'setup_intent_client_secret'
                );
                if (!clientSecret) return;

                const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret);

                let statusMsg;
                switch (setupIntent.status) {
                    case 'succeeded':
                        statusMsg = '🚀 Success! Your payment method has been saved.';
                        break;
                    case 'processing':
                        statusMsg = "⏳ Processing... We'll update you once it's complete.";
                        break;
                    case 'requires_payment_method':
                        statusMsg = '❌ Failed to save payment method. Please try again.';
                        break;
                    default:
                        statusMsg = '⚠️ Unknown status. Please contact support.';
                        break;
                }

                setMessage(statusMsg);
            } catch (error) {
                console.error('Error retrieving setup intent:', error);
                setMessage('❌ An error occurred while checking your payment status.');
            }
        };

        fetchStatus();
    }, [stripe]);

    if (!message) return null;

    return (
        <div className="alert mt-3 text-center p-4">
            <h5>{message}</h5>
            <p className="mt-3 text-muted">
                You may now continue back to the site. Click below to proceed.
            </p>
            <button className="btn btn-primary mt-2" onClick={() => onFinish?.()}>
                Continue
            </button>
        </div>
    );
}

export default function PaymentSetup({ documents, setGlobalDocuments, documentTypeConst }) {
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [clientSecret, setClientSecret] = useState({
        clientSecret: null,
        customerSessionClientSecret: null
    });
    const [showStatusOnly, setShowStatusOnly] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
    const [isGoCardlessCustomer, setIsGoCardlessCustomer] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    const user = useContext(UserContext);
    const hasDeposit = Boolean(user?.customerDeposits);

    useEffect(() => {
        const clientSecretFromURL = new URLSearchParams(window.location.search).get(
            'setup_intent_client_secret'
        );
        if (clientSecretFromURL) {
            setShowStatusOnly(true);
        }
    }, []);

    useEffect(() => {
        if (user?.email) {
            setIsGoCardlessCustomer(Boolean(customersGoCard[user.email]));
        }
    }, [user.email]);

    const fetchPaymentMethodStatus = useCallback(async () => {
        try {
            if (!user?.id) return;

            const response = await getPaymentMethods(user.id);
            if (Array.isArray(response?.data)) {
                setHasPaymentMethod(response.data.length > 0);
            } else {
                console.warn('Unexpected response from getPaymentMethods:', response);
                setHasPaymentMethod(false);
            }
        } catch (err) {
            console.error('Failed to fetch payment method status:', err);
            setHasPaymentMethod(false);
        }
    }, [user.id]);

    useEffect(() => {
        fetchPaymentMethodStatus();
    }, [fetchPaymentMethodStatus]);

    const fetchSetupIntent = useCallback(async () => {
        try {
            if (!user?.id) return;
            setErrorMessage(null);
            const data = await getSetupIntent(user.id);
            setClientSecret({
                clientSecret: data.clientSecret,
                customerSessionClientSecret: data.customerSessionClientSecret
            });
            setShowPaymentForm(true);
        } catch (error) {
            console.error('Error fetching setup intent:', error);
            setErrorMessage('Failed to start payment setup. Please try again later.');
        }
    }, [user.id]);

    const handleRedirectBack = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('setup_intent_client_secret');
        window.history.replaceState({}, document.title, url.toString());
        setShowStatusOnly(false);
    };

    const stripeOptions = {
        clientSecret: clientSecret.clientSecret,
        customerSessionClientSecret: clientSecret.customerSessionClientSecret,
        appearance: {
            layout: {
                type: 'accordion',
                defaultCollapsed: false,
                radios: false,
                spacedAccordionItems: true
            }
        }
    };

    return (
        <>
            {showStatusOnly ? (
                <Elements stripe={stripePromise}>
                    <PaymentStatusFeedback onFinish={handleRedirectBack} />
                </Elements>
            ) : (
                <>
                    {hasDeposit ? (
                        !showPaymentForm && (
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
                                <div className="flex-grow-1" style={{ flexBasis: '60%' }}>
                                    <div className="text-muted mb-3">
                                        {isGoCardlessCustomer ? (
                                            <>
                                                <p>
                                                    You are currently set up for Direct Debit to
                                                    automatically pay your future invoices.
                                                </p>
                                                <p>
                                                    If you wish to change your preferred payment
                                                    method, please contact us at
                                                    <a href="mailto:info@school-space.org">
                                                        {' '}
                                                        info@school-space.org
                                                    </a>
                                                    .
                                                </p>
                                            </>
                                        ) : hasPaymentMethod ? (
                                            <>
                                                <p>
                                                    Your preferred payment method is securely
                                                    stored.
                                                </p>
                                                <p>
                                                    By entering your payment details, you have
                                                    authorised us to charge this payment method for
                                                    current and future invoices, in line with our
                                                    usual invoice payment terms. You can change your
                                                    payment details at any time.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p>
                                                    To make sure your bookings are reserved as
                                                    quickly and easily as possible, please enter
                                                    your preferred payment details below.
                                                </p>
                                                <p>
                                                    By entering your payment details below, you
                                                    authorise School Space to use these payment
                                                    details for any future events that you book in
                                                    line with our usual invoice payment terms. You
                                                    can change your payment details at any time.
                                                </p>
                                                <p>
                                                    The next time you book with us, we'll use your
                                                    securely stored payment details to charge the
                                                    balance of your invoice on the invoice due date.
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    {errorMessage && (
                                        <div className="alert alert-danger mt-2">
                                            <strong>{errorMessage}</strong>
                                        </div>
                                    )}

                                    {!isGoCardlessCustomer && (
                                        <>
                                            {!hasPaymentMethod && (
                                                <div className="mt-2 mb-2">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={e =>
                                                                setIsChecked(e.target.checked)
                                                            }
                                                        />
                                                        <span className="text-muted mb-2 ml-2">
                                                            I have read and agree to the{' '}
                                                            <a
                                                                href="https://school-space.org/terms-conditions"
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Terms and Conditions
                                                            </a>
                                                        </span>
                                                    </label>
                                                </div>
                                            )}

                                            <button
                                                className="btn btn-primary mt-2"
                                                style={{
                                                    backgroundColor: '#3e9bd9',
                                                    color: 'white',
                                                    marginBottom: '10px'
                                                }}
                                                onClick={fetchSetupIntent}
                                                type="button"
                                                disabled={!hasPaymentMethod && !isChecked}
                                            >
                                                {hasPaymentMethod
                                                    ? 'Update Payment Method'
                                                    : 'Set Up Payment Method'}
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div
                                    className="flex-shrink-0"
                                    style={{ flexBasis: '35%', minWidth: '250px' }}
                                >
                                    <Deposit
                                        documents={documents}
                                        setGlobalDocuments={setGlobalDocuments}
                                        documentTypeConst={documentTypeConst}
                                    />
                                </div>
                            </div>
                        )
                    ) : (
                        <Deposit
                            documents={documents}
                            setGlobalDocuments={setGlobalDocuments}
                            documentTypeConst={documentTypeConst}
                        />
                    )}

                    {clientSecret.clientSecret && showPaymentForm && (
                        <Elements stripe={stripePromise} options={stripeOptions}>
                            <SetupForm setShowPaymentForm={setShowPaymentForm} />
                        </Elements>
                    )}
                </>
            )}
        </>
    );
}
